import {NewJoinerChecklistItemFilter, NewJoinerType} from "../../api/Model";
import {IconButton, Stack, SvgIconTypeMap, SxProps, Theme, Tooltip, Typography, TypographyProps} from "@mui/material";
import {blue, green, grey, orange, pink} from "@mui/material/colors";
import BusinessIcon from '@mui/icons-material/Business';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import React, {MouseEventHandler, ReactNode} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {newJoinerChecklistItemFilterGroup} from "./NewJoinerApi";
import {OverridableComponent} from "@mui/material/OverridableComponent";

const NEW_JOINER_TYPE_LABELS: Record<NewJoinerType, { label: string, sx: SxProps<Theme> }> = {
    [NewJoinerType.InternationalRelocation]: {
        label: 'International Relocation',
        sx: {
            backgroundColor: orange[200],
            color: 'black',
        }
    },
    [NewJoinerType.LocalRelocation]: {
        label: 'Local Relocation',
        sx: {
            backgroundColor: orange[50],
            color: 'black',
        }
    },
    [NewJoinerType.NewFTE]: {
        label: 'FTE',
        sx: {
            backgroundColor: blue[800],
            color: 'white',
        }
    },
    [NewJoinerType.NewIntern]: {
        label: 'Intern',
        sx: {
            backgroundColor: green[200],
            color: 'black',
        }
    },
    [NewJoinerType.InternToFTE]: {
        label: 'Intern → FTE',
        sx: {
            color: 'white',
            background: `linear-gradient(to right, ${green[200]}, ${blue[800]})`,
        }
    },
    [NewJoinerType.ReturnFromParentalLeave]: {
        label: 'From Parental Leave',
        sx: {
            backgroundColor: pink[100],
            color: 'black',
        }
    },
};

function NewJoinerTag({tooltip, onDelete, children, ...props}: {
    tooltip?: ReactNode,
    onDelete?: MouseEventHandler<HTMLButtonElement>,
    children: React.ReactNode,
} & TypographyProps) {
    return (
        <Tooltip title={tooltip}>
            <Typography
                variant="caption"
                {...props}
                sx={{
                    px: 1,
                    py: "1px",
                    borderRadius: "4px",
                    cursor: 'default',
                    ...props.sx,
                }}
            >
                <Stack direction="row" alignItems="center" gap="4px" component="span">
                    {children}
                    {onDelete && (
                        <IconButton
                            onClick={onDelete}
                            sx={{ml: 1, width: '16px', height: '16px'}}
                        >
                            <CloseIcon sx={{fontSize: '14px', color: (props.sx as any)?.['color']}}/>
                        </IconButton>
                    )}
                </Stack>
            </Typography>
        </Tooltip>
    );
}

export function NewJoinerTypeTag({type, tooltip, onDelete, ...props}: {
    type: NewJoinerType,
    tooltip?: ReactNode,
    onDelete?: MouseEventHandler<HTMLButtonElement>,
} & TypographyProps) {
    const newJoinerTypeStyle = NEW_JOINER_TYPE_LABELS[type];
    return (
        <NewJoinerTag
            tooltip={tooltip}
            onDelete={onDelete}
            {...props}
            sx={{
                ...newJoinerTypeStyle.sx,
                ...props.sx as any
            }}
        >{newJoinerTypeStyle.label}</NewJoinerTag>
    );
}

export function NewJoinerRemoteTag({remote, tooltip, onDelete, ...props}: {
    remote: boolean,
    tooltip?: ReactNode,
    onDelete?: MouseEventHandler<HTMLButtonElement>,
} & TypographyProps) {
    const params: {
        fgColor: string,
        bgColor: string,
        tooltip?: ReactNode,
        icon: OverridableComponent<SvgIconTypeMap>,
        label: string,
    } = remote ? {
        fgColor: 'white',
        bgColor: grey[800],
        tooltip: tooltip ?? 'Works remotely',
        icon: HomeWorkIcon,
        label: 'Remote',
    } : {
        fgColor: 'black',
        bgColor: grey[100],
        tooltip: tooltip ?? 'Works from the office',
        icon: BusinessIcon,
        label: 'Office',
    }

    const Icon = params.icon;

    return (
        <NewJoinerTag
            tooltip={params.tooltip}
            onDelete={onDelete}
            {...props}
            sx={{backgroundColor: params.bgColor, color: params.fgColor, ...props.sx}}
        >
            <Stack direction="row" alignItems="center" gap="4px" component="span">
                <Icon sx={{fontSize: "14px", mt: '-2px'}}/>
                {params.label}
            </Stack>
        </NewJoinerTag>
    );
}

export function NewJoinerChecklistItemFilterTag({filter, tooltip, onDelete, ...props}: {
    filter: NewJoinerChecklistItemFilter,
    tooltip?: ReactNode,
    onDelete?: MouseEventHandler<HTMLButtonElement>,
} & TypographyProps) {
    const group = newJoinerChecklistItemFilterGroup[filter];

    return group.group === 'work' ? (
        <NewJoinerRemoteTag remote={group.remote} tooltip={tooltip} onDelete={onDelete} {...props}/>
    ) : (
        <NewJoinerTypeTag type={group.type} tooltip={tooltip} onDelete={onDelete} {...props}/>
    );
}
